import type { JourneyPhase, UpsertJourneyPhase } from "@care-by-ella/contracts";
import { API_BASE, apiFetch, tenantHeader, withCredentials } from "./api";
import { jsonOr401 } from "./clinician-api";

// TES-209 — clinical journey-phase authoring client, mirroring journeys-api.ts.
// Auth rides the httpOnly clinician session cookie, scoped by tenantHeader().

function commonHeaders(): Record<string, string> {
  return { "Content-Type": "application/json", ...tenantHeader() };
}

export class JourneyPhaseApiError extends Error {}

async function jsonOrError<T>(res: Response): Promise<T> {
  if (res.status === 401 || res.status === 403) {
    return jsonOr401<T>(res);
  }
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { message?: unknown; error?: unknown };
      const msg = body.message ?? body.error;
      if (typeof msg === "string" && msg.trim()) detail = msg;
      else if (Array.isArray(msg) && msg.length) detail = msg.join("; ");
    } catch {
      // non-JSON body — keep the status-code detail
    }
    throw new JourneyPhaseApiError(detail);
  }
  return (await res.json()) as T;
}

const BASE = `${API_BASE}/employee-api/v1/journey-phases`;

export async function listJourneyPhases(): Promise<JourneyPhase[]> {
  const res = await apiFetch(BASE, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function fetchJourneyPhase(key: string): Promise<JourneyPhase> {
  const res = await apiFetch(`${BASE}/${key}`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function createJourneyPhase(
  body: UpsertJourneyPhase,
): Promise<JourneyPhase> {
  const res = await apiFetch(BASE, {
    method: "POST",
    headers: commonHeaders(),
    body: JSON.stringify(body),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function updateJourneyPhase(
  key: string,
  body: UpsertJourneyPhase,
): Promise<JourneyPhase> {
  const res = await apiFetch(`${BASE}/${key}`, {
    method: "PUT",
    headers: commonHeaders(),
    body: JSON.stringify(body),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function publishJourneyPhase(key: string): Promise<JourneyPhase> {
  const res = await apiFetch(`${BASE}/${key}/publish`, {
    method: "POST",
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function archiveJourneyPhase(key: string): Promise<JourneyPhase> {
  const res = await apiFetch(`${BASE}/${key}/archive`, {
    method: "POST",
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}
